/**
 * Was passiert, wenn jemand anderes zuerst gespeichert hat.
 *
 * ## Warum es das gibt
 *
 * Der Kunde am 27.08.2026:
 *
 * > „Wenn jemand anderes eine Speicherung macht, dann sollte bei dem anderen
 * > nichts passieren."
 *
 * Die eine Hälfte davon ist der Konfliktschutz der Schnittstelle: seit
 * demselben Tag prüft jeder Schreibweg den Stand, auf dem die Änderung beruht,
 * und antwortet mit **409**, statt zu überschreiben.
 *
 * Die andere Hälfte fehlte. In `apps/web/src` gab es **keine einzige** Stelle,
 * die `status === 409` auswertete — die Nummer kam an (`klient.ts`, `ApiFehler`
 * trägt sie), sie wurde nur weggeworfen. Was der Benutzer sah, war der
 * Servertext in derselben roten Meldung wie ein Tippfehler, und danach:
 *
 * - Die Eingaben standen noch da — gut.
 * - Die **Version** aber auch. Jeder weitere Klick auf „Speichern" schickte
 *   dieselbe veraltete mit und bekam wieder 409.
 * - Der Rat der Meldung lautete „bitte neu laden". Neu laden heißt hier: alle
 *   Eingaben weg.
 *
 * Eine Sackgasse mit einer Anleitung, die die Arbeit kostet, die sie retten
 * soll.
 *
 * ## Was dieser Baustein tut
 *
 * Er hält den Konflikt fest, statt ihn zu melden. Das Formular bleibt
 * unverändert stehen — kein Zustand wird zurückgesetzt, keine Eingabe
 * verworfen. Darüber erscheint eine Leiste mit dreierlei:
 *
 * 1. **Wer wann was geändert hat**, Feld für Feld, aus `iam.Aenderung`. Echte
 *    Daten; wo keine stehen, sagt die Leiste das und behauptet nichts.
 * 2. **„Meine Änderung trotzdem speichern"** — mit dem **neuen** Stand als
 *    Grundlage. Das ist die bewusste Entscheidung, den fremden Wert zu
 *    ersetzen, und sie ist nach dem Lesen des Unterschieds eine andere als
 *    vorher.
 * 3. **„Fremden Stand übernehmen"** — verwirft die eigenen Eingaben, und sagt
 *    das, bevor es passiert.
 *
 * ## Was er nicht tut
 *
 * Er führt nicht zusammen. Zwei Fassungen einer Rolle automatisch zu mischen
 * hieße, eine Entscheidung zu treffen, die niemand getroffen hat — bei
 * Berechtigungen die falsche Stelle dafür. Er zeigt beide und lässt wählen.
 */

import { useCallback, useState, type ReactNode } from 'react';
import { ApiFehler, holen } from '../api/klient';
import { tokenHolen } from '../anmeldung/msal';

/* -------------------------------------------------------------- Zustand */

/** Eine Änderung, die ein anderer gemacht hat. */
export interface Fremdaenderung {
  feld: string | null;
  wertVorher: string | null;
  wertNachher: string | null;
  akteurName: string | null;
  akteurKennung: string;
  zeitpunkt: string;
}

export interface Konflikt {
  /** Der Wortlaut des Servers — er nennt den Gegenstand. */
  meldung: string;
  /** Der Stand, auf dem weitergearbeitet werden kann. `null`, solange unbekannt. */
  neueVersion: string | null;
  /** Was der andere geändert hat. Leer heißt: nicht ermittelbar, nicht „nichts". */
  fremd: Fremdaenderung[];
  /** Konnte der fremde Stand überhaupt gelesen werden? */
  gelesen: boolean;
}

/**
 * Erkennt einen Konflikt und holt dazu, was sich sagen lässt.
 *
 * `neueVersion` und `fremd` kommen aus zwei Abrufen, die beide fehlschlagen
 * dürfen: der Konflikt bleibt auch dann ein Konflikt, und die Leiste sagt dann
 * eben weniger. Ein Baustein, der ohne seine Zusatzangaben gar nichts anzeigt,
 * wäre in genau dem Fall stumm, in dem er gebraucht wird.
 */
export function useKonfliktweg(mandant: string) {
  const [konflikt, setzeKonflikt] = useState<Konflikt | null>(null);

  /**
   * Prüft einen Fehler auf 409 und baut daraus den Konflikt.
   *
   * @returns `true`, wenn es ein Konflikt war — dann ist er behandelt und der
   *   Aufrufer meldet **nichts** weiter. `false` bei jedem anderen Fehler; den
   *   meldet der Aufrufer wie bisher.
   */
  const auffangen = useCallback(async (
    fehler: unknown,
    /** Woher der neue Stand kommt. Gibt `null` zurück, wenn er nicht lesbar ist. */
    standHolen: () => Promise<string | null>,
    /** Objektart und Kennung für das Änderungsprotokoll. */
    objekt: { objektart: string; objektId: string } | null,
  ): Promise<boolean> => {
    if (!(fehler instanceof ApiFehler) || fehler.status !== 409) return false;

    let neueVersion: string | null = null;
    let gelesen = false;
    try {
      neueVersion = await standHolen();
      gelesen = true;
    } catch {
      /* Der fremde Stand ist nicht lesbar — der Konflikt bleibt trotzdem
         einer. Die Leiste sagt dann, dass sie ihn nicht kennt. */
    }

    let fremd: Fremdaenderung[] = [];
    if (objekt) {
      try {
        const antwort = await holen<{ zeilen: Fremdaenderung[] }>(
          `/api/aenderungen?seite=1&groesse=10&objektart=${encodeURIComponent(objekt.objektart)}`
          + `&objektId=${encodeURIComponent(objekt.objektId)}`,
          { mandant, token: await tokenHolen() },
        );
        fremd = antwort.zeilen ?? [];
      } catch {
        /* Wer den Konflikt sieht, darf das Protokoll womöglich nicht lesen
           (`admin.audit`). Das ist kein Fehler dieser Stelle. */
      }
    }

    setzeKonflikt({ meldung: fehler.message, neueVersion, fremd, gelesen });
    return true;
  }, [mandant]);

  const schliessen = useCallback(() => setzeKonflikt(null), []);

  return { konflikt, auffangen, schliessen };
}

/* -------------------------------------------------------------- Anzeige */

/** Datum und Uhrzeit, wie sie im Rest der Anwendung stehen. */
function zeitpunkt(roh: string): string {
  const d = new Date(roh);
  if (Number.isNaN(d.getTime())) return roh;
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function wert(w: string | null): string {
  if (w === null) return '— leer —';
  if (w.trim() === '') return '— leer —';
  return w;
}

export interface KonfliktleisteProps {
  konflikt: Konflikt;
  /** Beschriftung des Gegenstands, z. B. „Rolle MSP-LTG-01". */
  gegenstand: string;
  /**
   * Noch einmal senden — mit dem neuen Stand. `undefined`, solange keiner
   * bekannt ist; dann bleibt der Knopf gesperrt und sagt, warum.
   */
  onTrotzdem: ((neueVersion: string) => void) | undefined;
  /** Eigene Eingaben verwerfen und den fremden Stand laden. */
  onUebernehmen: () => void;
  laeuft?: boolean;
}

/**
 * Die Leiste über dem Formular.
 *
 * Sie ersetzt keine Meldung, sie steht **im** Formular: eine Meldung
 * verschwindet, und mit ihr die Entscheidung, die getroffen werden muss.
 */
export function Konfliktleiste({
  konflikt, gegenstand, onTrotzdem, onUebernehmen, laeuft = false,
}: KonfliktleisteProps): ReactNode {
  const { meldung, neueVersion, fremd, gelesen } = konflikt;

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        border: '1px solid var(--brand-strong)',
        borderLeftWidth: '3px',
        borderRadius: 'var(--r-md)',
        background: 'var(--surface-raised)',
        padding: 'var(--raum-4)',
        marginBottom: 'var(--raum-4)',
        display: 'grid',
        gap: 'var(--raum-3)',
      }}
    >
      <div>
        <strong style={{ color: 'var(--brand-strong)' }}>
          {gegenstand} wurde inzwischen von jemand anderem gespeichert
        </strong>
        <p style={{ margin: 'var(--raum-2) 0 0', color: 'var(--text-secondary)' }}>
          {meldung}
        </p>
        <p style={{ margin: 'var(--raum-2) 0 0', color: 'var(--text-secondary)' }}>
          <strong>Ihre Eingaben stehen noch.</strong> Nichts davon ist verloren —
          entscheiden Sie, was gelten soll.
        </p>
      </div>

      {fremd.length > 0 && (
        <div>
          <div style={{
            fontSize: '13px', color: 'var(--text-secondary)',
            marginBottom: 'var(--raum-2)',
          }}>
            Was inzwischen geändert wurde:
          </div>
          <ul style={{ margin: 0, paddingLeft: 'var(--raum-5)', display: 'grid', gap: 'var(--raum-1)' }}>
            {fremd.map((a, i) => (
              <li key={`${a.zeitpunkt}-${a.feld ?? ''}-${i}`}>
                <strong>{a.akteurName ?? a.akteurKennung}</strong>
                {' hat am '}
                {zeitpunkt(a.zeitpunkt)}
                {a.feld ? <> das Feld <strong>{a.feld}</strong> von </> : ' etwas geändert: von '}
                <span style={{ color: 'var(--text-secondary)' }}>{wert(a.wertVorher)}</span>
                {' auf '}
                <span style={{ color: 'var(--text-primary)' }}>{wert(a.wertNachher)}</span>
                {' gesetzt.'}
              </li>
            ))}
          </ul>
        </div>
      )}

      {fremd.length === 0 && (
        /* Ausdrücklich benannt statt weggelassen: „hier steht nichts" könnte
           sonst heißen, es sei nichts geändert worden — und das wäre die
           falscheste Auskunft an dieser Stelle. */
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
          Was genau geändert wurde, lässt sich hier nicht sagen — das
          Änderungsprotokoll ist nicht abrufbar. Der Stand in der Datenbank ist
          trotzdem ein anderer als der, auf dem Ihre Änderung beruht.
        </p>
      )}

      <div style={{ display: 'flex', gap: 'var(--raum-3)', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="knopf knopf--haupt"
          disabled={laeuft || !onTrotzdem || neueVersion === null}
          title={neueVersion === null
            ? 'Der neue Stand ist nicht abrufbar. Bitte die Seite neu laden.'
            : undefined}
          onClick={() => { if (onTrotzdem && neueVersion !== null) onTrotzdem(neueVersion); }}
        >
          Meine Änderung trotzdem speichern
        </button>
        <button
          type="button"
          className="knopf"
          disabled={laeuft}
          onClick={onUebernehmen}
        >
          Fremden Stand übernehmen
        </button>
      </div>

      <p style={{
        margin: 0, fontSize: '13px', color: 'var(--text-secondary)',
      }}>
        <strong>Trotzdem speichern</strong> ersetzt die Änderung des anderen —
        sie steht danach im Änderungsprotokoll und ist nicht verloren.
        {' '}
        <strong>Fremden Stand übernehmen</strong> verwirft <em>Ihre</em>{' '}
        Eingaben.
        {!gelesen && ' Der neue Stand konnte nicht gelesen werden.'}
      </p>
    </div>
  );
}
